import axios from 'axios';
import store from '@/store';

import { getUrlContext } from '@/helpers/AppHelpers';
import i18n from '@/js/i18n';

/**
 * "Classe" MessageHelpers:
 * On obtient un code de message
 * On tente de trouver le message dans la page courante
 * Si non trouvé, on utilise le code pour trouver le message
 * parmi la liste des messages dans l'App Magnolia
 * On envoie l'info au composant Message
 */
export default {
  /**
   * obtenirMessage: Retourne un message
   * @msgCode
   * @vueRoot On passe this.$root si c'est dans le contexte d'une page Magnolia
   * @return {message}
   */
  async obtenirMessage(msgCode, vueRoot = null) {
    return _obtenirMessage(msgCode, vueRoot);
  },

  /**
   * getMessagesPageCourante: Retourne un array de messages
   * @vueRoot On passe this.$root si c'est dans le contexte d'une page Magnolia
   * @return {messages}
   */
  async getMessagesPageCourante(vueRoot = null) {
    if (vueRoot != null) {
      try {
        const messages = [];
        // Dans PageLoader.vue, nous devons avoir : ref="editablePage"
        const contentMessage = vueRoot.$children[0].$children.find(
          (el) => el.$refs && el.$refs.editablePage != undefined
        ).$refs.editablePage.content.message;

        // eslint-disable-next-line no-restricted-syntax
        for (const [key, val] of Object.entries(contentMessage)) {
          if (key.startsWith('message')) {
            messages.push({
              msgCode: val.msgCode,
              msgDetail: val.msgDetail,
              msgTitre: val.msgTitre,
              msgType: val.msgType,
            });
          }
        }

        return messages;
        // eslint-disable-next-line no-empty
      } catch (error) {
        console.error('Erreur de récupération des messages!', error);
      }
    }
    return null;
  },

  /**
   * Messages provenant de l'app message sont encapsulés dans des spans.
   * Changement des balises pour uniformiser le padding des affichages
   */
  nettoyerMessageSpanP(message) {
    let pattern = /<[\\/]?span>/;
    if (message.includes('<span>')) {
      message = message.replace(new RegExp(pattern, 'gm'), '');
    }
    if (message.includes('<p>')) {
      pattern = /<[\\/]?p>/;
      message = message.replace(new RegExp(pattern, 'gm'), '');
    }
    return message;
  },
};

// Retour d'un message garanti:
// - Dans la page courante
// - Dans le store
// - Dans l'app Message
// - Message inexistant par défaut
async function _obtenirMessage(msgCode, vueRoot) {
  let unMessage = null;

  unMessage = chercherMessagePageCourante(msgCode, vueRoot);
  if (!unMessage) {
    unMessage = chercherMessageStore(msgCode);
    if (!unMessage) {
      unMessage = await chercherMessageApp(msgCode);
      if (!unMessage) {
        // TODO: Peaufiner + EN/FR
        const msgDetail = `Message '${msgCode}' inexistant`;
        unMessage = {
          msgCode,
          msgDetail,
          msgTitre: 'Message inexistant',
          msgType: 'echec',
          msgInexistant: true,
        };
      }
    }
  }

  return unMessage;
}

/**
 * Permet de trouver le message dans la page courante
 * @return {boolean} true si le message a été trouvé, sinon false
 */
function chercherMessagePageCourante(msgCode, vueRoot) {
  if (vueRoot != null) {
    try {
      // Dans PageLoader.vue, nous devons avoir : ref="editablePage"
      const contentMessage = vueRoot.$children[0].$children.find(
        (el) => el.$refs && el.$refs.editablePage != undefined
      ).$refs.editablePage.content.message;

      // eslint-disable-next-line no-restricted-syntax
      for (const [key, val] of Object.entries(contentMessage)) {
        if (key.startsWith('message') && val?.msgCode === msgCode) {
          return {
            msgCode: val.msgCode,
            msgDetail: val.msgDetail,
            msgTitre: val.msgTitre,
            msgType: val.msgType,
          };
        }
      }

      // eslint-disable-next-line no-empty
    } catch (error) {}
  }

  return null;
}

/**
 * Permet de trouver le message dans store
 * @return {boolean} true si le message a été trouvé
 */
function chercherMessageStore(msgCode) {
  try {
    const listeMsg = store.getters['message/getListeMessages'];
    const message = listeMsg.find((aMessage) => aMessage.msgCode == msgCode);
    return message;
    // eslint-disable-next-line no-empty
  } catch (error) {}

  return null;
}

/**
 * Permet de trouver le message en faisant un appel à l'application
 */
async function chercherMessageApp(msgCode) {
  return axios
    .get(
      getUrlContext(`${process.env.VUE_APP_REST_JEL.replace('CONTENT_APP', 'message')}/${msgCode}`),
      {
        params: { lang: i18n.locale },
      }
    )
    .then((res) => {
      const msg = res.data;

      const unMessage = {
        msgCode: msg.name,
        msgDetail: msg.msgDetail,
        msgTitre: msg.msgTitre,
        msgType: msg.msgType,
      };

      store.commit('message/ADD_MESSAGE', unMessage); // ajouter le message dans le store
      return unMessage;
    })
    .catch((error) => {
      return null;
    });
}
